import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Divider,
  FormControl,
  Grid,
  TextFieldProps,
  Typography,
} from '@mui/material';

import { useAppSelector } from '../../../../../hooks';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../components/Inputs';
import {
  getQuiztopiyaCurriculumsForAdmin,
  getQuiztopiyaQuizzesForAdmin,
  getQuiztopiyaState,
  getQuiztopiyaSubTopicsForAdmin,
  getQuiztopiyaTopicsForAdmin,
  resetAdminCurriculums,
  resetAdminQuizzes,
  resetAdminSubTopics,
  resetAdminTopics,
  TGetQuizzesForAdmin,
} from '../../../../../store/V2/QuiztopiyaSlice';

type TSelectOption = {
  label: string;
  value: string;
};

type TSubjectSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedSubject: TSubjectSelectOption | null;
  selectedSubjectLevel: TSelectOption | null;
  selectedTopic: TSelectOption | null;
  selectedSubTopic: TSelectOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedCurriculum: null,
  selectedGrade: null,
  selectedSubject: null,
  selectedSubjectLevel: null,
  selectedTopic: null,
  selectedSubTopic: null,
};

const QuizFilterSection: React.FC = () => {
  const dispatch = useDispatch();

  // Store
  const quiztopiyaState = useAppSelector(getQuiztopiyaState);

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);
  const [subjectLevelOptions, setSubjectLevelOptions] = useState<
    Array<TSelectOption>
  >([]);
  const [topicOptions, setTopicOptions] = useState<Array<TSelectOption>>([]);
  const [subTopicOptions, setSubTopicOptions] = useState<Array<TSelectOption>>(
    []
  );

  useEffect(() => {
    dispatch(getQuiztopiyaCurriculumsForAdmin());

    return () => {
      dispatch(resetAdminCurriculums());
      dispatch(resetAdminTopics());
      dispatch(resetAdminSubTopics());
      dispatch(resetAdminQuizzes());
    };
  }, []);

  useEffect(() => {
    if (
      quiztopiyaState.adminCurriculums &&
      quiztopiyaState.adminCurriculums.length
    ) {
      const newCurriculumsOptions: TSelectOption[] = [];
      const newGradeOptions: TSelectOption[] = [];
      const newSubjectOptions: TSelectOption[] = [];
      const newSubjectLevelOptions: TSelectOption[] = [];

      quiztopiyaState.adminCurriculums.forEach((curriculumItem) => {
        newCurriculumsOptions.push({
          label: curriculumItem.name,
          value: curriculumItem.name,
        });
      });

      if (state.selectedCurriculum) {
        const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
          (item) => item.name === state.selectedCurriculum?.value
        );

        if (
          matchingCurriculumItem &&
          matchingCurriculumItem.grades &&
          matchingCurriculumItem.grades.length
        ) {
          matchingCurriculumItem.grades.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.name,
              value: gradeItem.name,
            });
          });

          if (state.selectedGrade) {
            const matchingGradeItem = matchingCurriculumItem.grades.find(
              (item) => item.name === state.selectedGrade?.value
            );

            if (
              matchingGradeItem &&
              matchingGradeItem.subjects &&
              matchingGradeItem.subjects.length
            ) {
              matchingGradeItem.subjects.forEach((subjectItem) => {
                newSubjectOptions.push({
                  label: subjectItem.name,
                  value: subjectItem.code,
                });
              });

              if (state.selectedSubject) {
                const matchingSubjectItem = matchingGradeItem.subjects.find(
                  (item) => item.code === state.selectedSubject?.value
                );

                if (
                  matchingSubjectItem &&
                  matchingSubjectItem.subjectLevels &&
                  matchingSubjectItem.subjectLevels.length
                ) {
                  matchingSubjectItem.subjectLevels.forEach(
                    (subjectLevelItem) => {
                      newSubjectLevelOptions.push({
                        label: subjectLevelItem.name,
                        value: subjectLevelItem.name,
                      });
                    }
                  );
                }
              }
            }
          }
        }
      }

      setCurriculumOptions(newCurriculumsOptions);
      setGradeOptions(newGradeOptions);
      setSubjectOptions(newSubjectOptions);
      setSubjectLevelOptions(newSubjectLevelOptions);
    }
  }, [quiztopiyaState.adminCurriculums]);

  useEffect(() => {
    if (quiztopiyaState.adminTopics && quiztopiyaState.adminTopics.length) {
      const newTopicOptions: TSelectOption[] = [];

      quiztopiyaState.adminTopics.forEach((topicItem) => {
        newTopicOptions.push({
          label: topicItem.name,
          value: topicItem._id,
        });
      });

      setTopicOptions(newTopicOptions);
    }
  }, [quiztopiyaState.adminTopics]);

  useEffect(() => {
    if (
      quiztopiyaState.adminSubTopics &&
      quiztopiyaState.adminSubTopics.length
    ) {
      const newSubTopicOptions: TSelectOption[] = [];

      quiztopiyaState.adminSubTopics.forEach((subTopicItem) => {
        newSubTopicOptions.push({
          label: subTopicItem.name,
          value: subTopicItem._id,
        });
      });

      setSubTopicOptions(newSubTopicOptions);
    }
  }, [quiztopiyaState.adminSubTopics]);

  useEffect(() => {
    if (
      state.selectedCurriculum &&
      state.selectedGrade &&
      state.selectedSubject &&
      state.selectedSubjectLevel
    ) {
      const apiData: TGetQuizzesForAdmin = {
        curriculum: state.selectedCurriculum.value,
        grade: state.selectedGrade.value,
        subject: state.selectedSubject.label,
        subjectLevel: state.selectedSubjectLevel.value,
        subjectCode: state.selectedSubject.value,
      };

      if (state.selectedTopic) {
        apiData.topicId = state.selectedTopic.value;
      }

      if (state.selectedSubTopic) {
        apiData.subTopicId = state.selectedSubTopic.value;
      }

      dispatch(getQuiztopiyaQuizzesForAdmin(apiData));
    }
  }, [state]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedGrade: null,
      selectedSubject: null,
      selectedSubjectLevel: null,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    const newGradeOptions: TSelectOption[] = [];

    if (value) {
      const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
        (item) => item.name === value.value
      );

      if (
        matchingCurriculumItem &&
        matchingCurriculumItem.grades &&
        matchingCurriculumItem.grades.length
      ) {
        matchingCurriculumItem.grades.forEach((gradeItem) => {
          newGradeOptions.push({
            label: gradeItem.name,
            value: gradeItem.name,
          });
        });
      }
    }

    setGradeOptions(newGradeOptions);
    setSubjectOptions([]);
    setSubjectLevelOptions([]);
    setTopicOptions([]);
    setSubTopicOptions([]);

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
    dispatch(resetAdminQuizzes());
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
      selectedSubject: null,
      selectedSubjectLevel: null,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    const newSubjectOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
        (item) => item.name === state.selectedCurriculum?.value
      );

      if (
        value &&
        matchingCurriculumItem &&
        matchingCurriculumItem.grades &&
        matchingCurriculumItem.grades.length
      ) {
        const matchingGradeItem = matchingCurriculumItem.grades.find(
          (item) => item.name === value.value
        );

        if (
          matchingGradeItem &&
          matchingGradeItem.subjects &&
          matchingGradeItem.subjects.length
        ) {
          matchingGradeItem.subjects.forEach((subjectItem) => {
            newSubjectOptions.push({
              label: subjectItem.name,
              value: subjectItem.code,
            });
          });
        }
      }
    }

    setSubjectOptions(newSubjectOptions);
    setSubjectLevelOptions([]);
    setTopicOptions([]);
    setSubTopicOptions([]);

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
    dispatch(resetAdminQuizzes());
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubject: value,
      selectedSubjectLevel: null,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    const newSubjectLevelOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
        (item) => item.name === state.selectedCurriculum?.value
      );

      if (
        state.selectedGrade &&
        matchingCurriculumItem &&
        matchingCurriculumItem.grades &&
        matchingCurriculumItem.grades.length
      ) {
        const matchingGradeItem = matchingCurriculumItem.grades.find(
          (item) => item.name === state.selectedGrade?.value
        );

        if (
          value &&
          matchingGradeItem &&
          matchingGradeItem.subjects &&
          matchingGradeItem.subjects.length
        ) {
          const matchingSubjectItem = matchingGradeItem.subjects.find(
            (item) => item.code === value.value
          );

          if (
            matchingSubjectItem &&
            matchingSubjectItem.subjectLevels &&
            matchingSubjectItem.subjectLevels.length
          ) {
            matchingSubjectItem.subjectLevels.forEach((subjectLevelItem) => {
              newSubjectLevelOptions.push({
                label: subjectLevelItem.name,
                value: subjectLevelItem.name,
              });
            });
          }
        }
      }
    }

    setSubjectLevelOptions(newSubjectLevelOptions);
    setTopicOptions([]);
    setSubTopicOptions([]);

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
    dispatch(resetAdminQuizzes());
  };

  const handleSubjectLevelChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubjectLevel: value,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    setTopicOptions([]);
    setSubTopicOptions([]);

    if (value && state.selectedSubject) {
      dispatch(
        getQuiztopiyaTopicsForAdmin({
          subjectCode: state.selectedSubject.value,
          subjectLevel: value.label,
        })
      );
    }

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
  };

  const handleTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedTopic: value,
      selectedSubTopic: null,
    }));

    if (value) {
      dispatch(
        getQuiztopiyaSubTopicsForAdmin({
          topicId: value.value,
        })
      );
    }

    setSubTopicOptions([]);

    dispatch(resetAdminSubTopics());
  };

  const handleSubTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubTopic: value,
    }));
  };

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ borderStyle: 'dashed' }} />
      </Grid>
      <Grid item xs={12}>
        <Typography>Quiz Details</Typography>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            value={state.selectedCurriculum}
            options={curriculumOptions}
            onChange={handleCurriculumChange}
            popupIcon={TPAutoCompleteArrowIconComponent}
            loading={quiztopiyaState.status === 'loading'}
            isOptionEqualToValue={(option, value) =>
              option.label === value.value
            }
            getOptionKey={(option: TSelectOption) => option.value}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                size="small"
                label="Curriculum *"
                placeholder="Curriculum"
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            value={state.selectedGrade}
            options={gradeOptions}
            onChange={handleGradeChange}
            disabled={!state.selectedCurriculum}
            popupIcon={TPAutoCompleteArrowIconComponent}
            loading={quiztopiyaState.status === 'loading'}
            isOptionEqualToValue={(option, value) =>
              option.label === value.value
            }
            getOptionKey={(option: TSelectOption) => option.value}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                size="small"
                label="Grade *"
                placeholder="Grade"
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            value={state.selectedSubject}
            options={subjectOptions}
            onChange={handleSubjectChange}
            disabled={!state.selectedGrade}
            popupIcon={TPAutoCompleteArrowIconComponent}
            loading={quiztopiyaState.status === 'loading'}
            getOptionKey={(option: TSelectOption) => option.value}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                size="small"
                label="Subject *"
                placeholder="Subject"
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            value={state.selectedSubjectLevel}
            options={subjectLevelOptions}
            onChange={handleSubjectLevelChange}
            disabled={!state.selectedSubject}
            popupIcon={TPAutoCompleteArrowIconComponent}
            loading={quiztopiyaState.status === 'loading'}
            isOptionEqualToValue={(option, value) =>
              option.label === value.value
            }
            getOptionKey={(option: TSelectOption) => option.value}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                size="small"
                label="Subject Level *"
                placeholder="Subject Level"
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            value={state.selectedTopic}
            options={topicOptions}
            onChange={handleTopicChange}
            disabled={!state.selectedSubjectLevel}
            popupIcon={TPAutoCompleteArrowIconComponent}
            loading={quiztopiyaState.status === 'loading'}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                size="small"
                label="Topic"
                placeholder="Topic"
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            value={state.selectedSubTopic}
            options={subTopicOptions}
            onChange={handleSubTopicChange}
            disabled={!state.selectedTopic}
            popupIcon={TPAutoCompleteArrowIconComponent}
            loading={quiztopiyaState.status === 'loading'}
            renderInput={(params) => (
              <TPTextField
                {...params}
                {...textFieldProps}
                size="small"
                label="Subtopic"
                placeholder="Subtopic"
              />
            )}
          />
        </FormControl>
      </Grid>
    </>
  );
};

export default QuizFilterSection;
